import React from "react";

import * as types from "types";
import MessageInput from "components/MessageInput";
import MessageHistory from "components/MessageHistory";
import GitlabCorner from "components/GitlabCorner";
import useGetAccessToken from "components/Login/useGetAccessToken";
import useChatStream from "services/cortevaChain/hooks/useChatStream";
import { useStickToBottom } from "hooks/useStickToBottom";
import Introduction from "./Introduction";
import { systemMessage } from "./systemMessage";

const MODEL_NAME = types.ModelName.GPT_41;

const datasetCreatorPersona: types.Persona = {
  id: 0,
  name: "Dataset Creator",
  modelName: MODEL_NAME,
  imageCapable: false,
  active: true,
  codeExecutionCapable: false,
  systemMessage: systemMessage.content,
};

export default function DatasetCreator() {
  const [messages, setMessages] = React.useState<types.Message[]>([]);
  const [userInput, setUserInput] = React.useState("");

  const chatWindowRef = React.useRef<null | HTMLDivElement>(null);
  const { contentRef } = useStickToBottom({
    containerRef: chatWindowRef,
    messages,
  });

  const getAccessToken = useGetAccessToken();

  // Restore the aborted user message into the draft so the user can edit it
  // and resend.
  const handleStreamAbort = React.useCallback((userMessageContent: string) => {
    setUserInput(userMessageContent);
  }, []);

  const streamState = useChatStream({
    chatThreadId: "",
    persona: datasetCreatorPersona,
    messages,
    setMessages,
    getAccessToken,
    onAbort: handleStreamAbort,
  });

  // TODO: handle errors better.
  if (streamState.status === "error") {
    console.error(streamState.error);
  }

  const handleUserMessage = React.useCallback(
    (message: types.UserMessage) => {
      if (streamState.status === "loading") return;
      setMessages((prev) => [...prev, message]);
    },
    [streamState.status]
  );

  const body =
    messages.length > 0 ? (
      <MessageHistory
        messages={messages}
        isStreaming={streamState.status === "loading"}
        contentRef={contentRef}
      />
    ) : (
      <Introduction />
    );

  return (
    <div className="chat vh-100-l vh-100-m">
      <GitlabCorner gitlabUrl="https://gitlab.research.vylor.com/research/ai-pioneers/corteva-chat/corteva-chat-ui/-/tree/sb/src/routes/Experiments/DatasetCreator" />
      {/* Header */}
      <div className="chat-header dn db-m db-l">
        <div
          style={{ backgroundColor: "#0072CE" }}
          className="flex items-center"
        >
          <p
            className="f5 b lh-copy white mv0 pt1 pb1 dib tc"
            style={{ flexGrow: 1 }}
          >
            {MODEL_NAME} (galileo)
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="chat-body" ref={chatWindowRef}>
        {body}
      </div>

      {/* Sticky footer */}
      <div className="chat-footer">
        <MessageInput
          persona={datasetCreatorPersona}
          userInput={userInput}
          setUserInput={setUserInput}
          disabled={streamState.status === "loading"}
          onStop={streamState.abort}
          onSend={handleUserMessage}
          toolAvailability={{
            searchEnabled: false,
            jupyterHubEnabled: false,
          }}
        />
      </div>
    </div>
  );
}
